import Phaser from 'phaser';
import {
  QUEUE_PEEK_USES,
  FONT_FAMILY,
  GAME_WIDTH,
  getStackY,
  getQueueY,
} from '../config/constants';
import { QueueSystem } from '../systems/QueueSystem';
import { primaryColor, totalAmmo } from '../entities/Attacker';
import { crispText } from './textUtils';
import { soundManager } from '../audio/SoundManager';

/**
 * Manages the "Peek" feature: a limited-use ability that reveals
 * upcoming attackers in the queue beyond the visible front rows.
 *
 * Extracted from GameScene to reduce god-object complexity.
 */
export class PeekManager {
  private scene: Phaser.Scene;
  private queue: QueueSystem;
  private isGameEnded: () => boolean;

  // State
  private usesRemaining = 0;
  private active = false;

  // UI elements
  private buttonBg!: Phaser.GameObjects.Graphics;
  private buttonLabel!: Phaser.GameObjects.Text;
  private overlayContainer?: Phaser.GameObjects.Container;
  private dismissZone?: Phaser.GameObjects.Zone;

  constructor(scene: Phaser.Scene, queue: QueueSystem, isGameEnded: () => boolean) {
    this.scene = scene;
    this.queue = queue;
    this.isGameEnded = isGameEnded;
  }

  /** Reset state for a new game. Call from GameScene.init(). */
  reset(): void {
    this.usesRemaining = QUEUE_PEEK_USES;
    this.active = false;
    this.overlayContainer = undefined;
    this.dismissZone = undefined;
  }

  /** Create the peek button UI. Call from GameScene.create(). */
  create(): void {
    const bx = GAME_WIDTH / 2;
    const by = Math.round((getStackY() + getQueueY()) / 2);
    const bw = 152;
    const bh = 44;

    this.buttonBg = this.scene.add.graphics().setDepth(10);
    this.drawButtonBg(true);

    this.buttonLabel = crispText(this.scene.add.text(bx, by, `Peek x${this.usesRemaining}`, {
      fontFamily: FONT_FAMILY,
      fontSize: '22px',
      color: '#8888cc',
      fontStyle: 'bold',
    })).setOrigin(0.5).setDepth(10);

    const zone = this.scene.add.zone(bx, by, bw, bh).setInteractive().setDepth(10);
    zone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      this.activate();
    });
  }

  // ── Internal ──────────────────────────────────────────────────────────

  private drawButtonBg(enabled: boolean): void {
    const bx = GAME_WIDTH / 2;
    const by = Math.round((getStackY() + getQueueY()) / 2);
    const bw = 152;
    const bh = 44;

    this.buttonBg.clear();
    const fillColor = enabled ? 0x1a1a30 : 0x111120;
    const fillAlpha = enabled ? 0.7 : 0.5;
    const strokeColor = enabled ? 0x5555aa : 0x333344;
    const strokeAlpha = enabled ? 0.6 : 0.4;
    this.buttonBg.fillStyle(fillColor, fillAlpha);
    this.buttonBg.fillRoundedRect(bx - bw / 2, by - bh / 2, bw, bh, 12);
    this.buttonBg.lineStyle(2, strokeColor, strokeAlpha);
    this.buttonBg.strokeRoundedRect(bx - bw / 2, by - bh / 2, bw, bh, 12);
  }

  private activate(): void {
    if (this.active || this.usesRemaining <= 0 || this.isGameEnded()) return;

    this.active = true;
    this.usesRemaining--;
    soundManager.peek();
    this.updateButton();
    this.showOverlay();

    // Fullscreen zone to dismiss on tap anywhere
    const gameHeight = this.scene.scale.height;
    this.dismissZone = this.scene.add.zone(GAME_WIDTH / 2, gameHeight / 2, GAME_WIDTH, gameHeight)
      .setInteractive()
      .setDepth(39); // just below overlay (40) so taps land here
    this.dismissZone.on('pointerdown', () => {
      this.dismiss();
    });
  }

  private showOverlay(): void {
    this.clearOverlay();

    const children: Phaser.GameObjects.GameObject[] = [];
    const numQ = this.queue.numLanes();

    // Dynamic lane sizing — fit within GAME_WIDTH with padding
    const maxPanelWidth = GAME_WIDTH - 32; // 16px padding each side
    const laneGap = numQ <= 3 ? 20 : (numQ <= 4 ? 14 : 10);
    const panelPadH = 24; // horizontal padding inside panel
    const availableForLanes = maxPanelWidth - panelPadH * 2;
    const laneWidth = Math.min(140, Math.floor((availableForLanes - (numQ - 1) * laneGap) / numQ));
    const totalLanesWidth = numQ * laneWidth + (numQ - 1) * laneGap;
    const panelWidth = totalLanesWidth + panelPadH * 2;
    const panelLeft = (GAME_WIDTH - panelWidth) / 2;
    const lanesStartX = panelLeft + panelPadH;

    // Sizing: compact for many queues, roomier for few
    const itemSize = numQ <= 3 ? 22 : (numQ <= 4 ? 20 : 18);
    const itemSpacing = numQ <= 3 ? 30 : (numQ <= 4 ? 28 : 26);
    const maxShow = 5;
    const headerHeight = 50; // title area
    const labelHeight = 24;  // Q1/Q2 labels
    const contentHeight = maxShow * itemSpacing;
    const footerHeight = 28; // dismiss hint
    const panelHeight = headerHeight + labelHeight + contentHeight + footerHeight;

    const panelTop = getQueueY() - panelHeight - 12;

    // Dark semi-transparent panel background
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x0d0d1a, 0.92);
    bg.fillRoundedRect(panelLeft, panelTop, panelWidth, panelHeight, 14);
    bg.lineStyle(2, 0x4444aa, 0.5);
    bg.strokeRoundedRect(panelLeft, panelTop, panelWidth, panelHeight, 14);
    children.push(bg);

    // Title
    const title = crispText(this.scene.add.text(GAME_WIDTH / 2, panelTop + 22, 'UPCOMING', {
      fontFamily: FONT_FAMILY,
      fontSize: '18px',
      color: '#6666aa',
      fontStyle: 'bold',
    })).setOrigin(0.5);
    children.push(title);

    // Dismiss hint
    const hint = crispText(this.scene.add.text(GAME_WIDTH / 2, panelTop + panelHeight - 14, 'tap anywhere to close', {
      fontFamily: FONT_FAMILY,
      fontSize: '13px',
      color: '#555577',
    })).setOrigin(0.5);
    children.push(hint);

    // Lane separator lines and content
    const contentStartY = panelTop + headerHeight;

    for (let lane = 0; lane < numQ; lane++) {
      const laneX = lanesStartX + lane * (laneWidth + laneGap);
      const laneCenterX = laneX + laneWidth / 2;

      // Lane label (Q1, Q2, ...)
      const labelText = numQ > 1 ? `Q${lane + 1}` : '';
      if (labelText) {
        const label = crispText(this.scene.add.text(laneCenterX, contentStartY + 2, labelText, {
          fontFamily: FONT_FAMILY,
          fontSize: '14px',
          color: '#5555aa',
          fontStyle: 'bold',
        })).setOrigin(0.5, 0);
        children.push(label);
      }

      // Subtle vertical separator between lanes (not after last)
      if (lane < numQ - 1 && numQ > 1) {
        const sepX = laneX + laneWidth + laneGap / 2;
        const sepGraphics = this.scene.add.graphics();
        sepGraphics.lineStyle(1, 0x333366, 0.4);
        sepGraphics.lineBetween(sepX, contentStartY, sepX, contentStartY + labelHeight + contentHeight);
        children.push(sepGraphics);
      }

      // Show upcoming attackers per lane (items 3-7, beyond the visible front 3)
      const q = this.queue.getQueueLane(lane);
      const startIdx = 3;
      let shown = 0;

      for (let j = 0; j < maxShow; j++) {
        const idx = startIdx + j;
        if (idx >= q.length) break;

        const attacker = q[idx];
        const itemY = contentStartY + labelHeight + 4 + j * itemSpacing + itemSpacing / 2;

        const peekMystery = attacker.orbs?.every(o => o.mystery) ?? false;
        const peekTextureKey = peekMystery ? 'mystery' : `${primaryColor(attacker)}`;
        const circle = this.scene.add.image(laneCenterX - itemSize * 0.6, itemY, `attacker-${peekTextureKey}`);
        circle.setDisplaySize(itemSize, itemSize);
        children.push(circle);

        const ammoText = crispText(this.scene.add.text(laneCenterX + itemSize * 0.4, itemY, peekMystery ? '?' : `${totalAmmo(attacker)}`, {
          fontFamily: FONT_FAMILY,
          fontSize: `${Math.max(14, itemSize - 2)}px`,
          color: '#aaaacc',
        }), false).setOrigin(0, 0.5);
        children.push(ammoText);
        shown++;
      }

      if (shown === 0) {
        const emptyY = contentStartY + labelHeight + contentHeight / 2;
        const empty = crispText(this.scene.add.text(laneCenterX, emptyY, '-', {
          fontFamily: FONT_FAMILY,
          fontSize: '22px',
          color: '#333355',
        }), false).setOrigin(0.5);
        children.push(empty);
      }
    }

    const container = this.scene.add.container(0, 0, children);
    container.setDepth(40);
    container.setAlpha(0);

    // Fade in
    this.scene.tweens.add({
      targets: container,
      alpha: 1,
      duration: 150,
      ease: 'Power2',
    });

    this.overlayContainer = container;
  }

  private dismiss(): void {
    if (!this.active) return;
    this.active = false;

    if (this.dismissZone) {
      this.dismissZone.destroy();
      this.dismissZone = undefined;
    }

    if (this.overlayContainer) {
      const overlay = this.overlayContainer;
      this.scene.tweens.add({
        targets: overlay,
        alpha: 0,
        duration: 150,
        ease: 'Power2',
        onComplete: () => overlay.destroy(),
      });
      this.overlayContainer = undefined;
    }
  }

  private clearOverlay(): void {
    if (this.overlayContainer) {
      this.overlayContainer.destroy();
      this.overlayContainer = undefined;
    }
  }

  /** Clean up timers and overlays. Called on scene shutdown. */
  destroy(): void {
    if (this.dismissZone) {
      this.dismissZone.destroy();
      this.dismissZone = undefined;
    }
    if (this.overlayContainer) {
      this.overlayContainer.destroy();
      this.overlayContainer = undefined;
    }
    this.active = false;
  }

  private updateButton(): void {
    const enabled = this.usesRemaining > 0;
    this.buttonLabel.setText(`Peek x${this.usesRemaining}`);
    this.buttonLabel.setColor(enabled ? '#8888cc' : '#444455');
    this.drawButtonBg(enabled);
  }
}
